/**
 * One peer puck and one cluster, shared by the radar, the 2D map and the 3D map
 * (design hand-off "people on the map", 2026-09-14). The radar's geometry won
 * the audit; the fill carries IDENTITY (the person's own AVATAR_COLORS hue, the
 * same one their list row wears), never presence. Presence lives in words.
 *
 * The casing under every face is load-bearing: two of the ten identity hues
 * measure ~1.05:1 against the 3D ground and the accent stroke cannot rescue
 * them, so without the casing a puck sinks into the terrain.
 *
 * Three renderers, one geometry: React SVG (radar), SVG strings (MapLibre DOM
 * markers) and a canvas painter (three.js sprite textures) all read PUCK.
 */
import { getAvatarColorHex } from '../../components/Avatar';
import { getProfilePhotoUrl } from '../images/profilePhotoUrl';
import { findPeerInitial } from '../proximity/findPeerDisplayName';
import { glassFaceSvg, glassFaces, paintGlassFace } from './faceStyle';

export const PUCK = {
  face: 18, // r, 36px — identity fill
  casing: 19.4, // r, #120a1e, drawn under the face
  casingFill: '#120a1e',
  stroke: '#9a49d2', // brand cue
  strokeWidth: 1,
  ring: 21.5, // tracked hairline, clears the casing
  ringOpacity: 0.75,
  badge: 9,
  badgeOffset: 14,
  badgeFill: '#9a49d2',
  badgeText: '#08050f',
  clusterFill: '#5f2a8f', // the cluster's own mark: keeps a stack of two from reading as two people
  clusterStrokeWidth: 0.75,
  initial: { size: 12, weight: 600, spacing: 0.3, fill: '#fff' },
  box: 52, // SVG viewBox for a single puck (the ring needs 43; 52 leaves the offset back face room)
  photoPx: 72,
};

/** Cluster representatives: the tracked peer in front, else alphabetical; one other behind. */
export function pickClusterRepresentatives(
  peers,
  { nameOf = (p) => p.displayName, isTracked = (p) => Boolean(p.isTracked) } = {},
) {
  const sorted = [...peers].sort((a, b) => String(nameOf(a)).localeCompare(String(nameOf(b))));
  const tracked = sorted.find((p) => isTracked(p));
  if (tracked) {
    const others = sorted.filter((p) => !isTracked(p));
    return [tracked, others[0] ?? null];
  }
  return [sorted[0], sorted[1] ?? null];
}

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

/** Face SVG fragment centred at (cx, cy): casing, identity (or cluster) fill, stroke, initial or photo. */
export function faceSvg({ cx, cy, displayName, photoUrl, clipId, cluster = false }) {
  if (glassFaces()) {
    // glass and the person's colour ring (lib/map/faceStyle.js); a photo sits inside the ring
    const r = PUCK.casing,
      inner = r - Math.max(1.5, r * 0.13);
    const src = !cluster && photoUrl ? getProfilePhotoUrl(photoUrl, PUCK.photoPx) : null;
    const base = glassFaceSvg({ cx, cy, r, displayName, cluster, gid: `${clipId}-g` });
    if (src)
      return (
        base +
        `<defs><clipPath id="${clipId}"><circle cx="${cx}" cy="${cy}" r="${inner}"/></clipPath></defs>` +
        `<image href="${esc(src)}" x="${cx - inner}" y="${cy - inner}" width="${inner * 2}" height="${inner * 2}" clip-path="url(#${clipId})" preserveAspectRatio="xMidYMid slice"/>`
      );
    if (cluster) return base;
    return (
      base +
      `<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-size="${PUCK.initial.size + 2}" font-weight="700" fill="#fff">${esc(findPeerInitial(displayName || '?'))}</text>`
    );
  }
  const fill = cluster ? PUCK.clusterFill : getAvatarColorHex(displayName);
  const sw = cluster ? PUCK.clusterStrokeWidth : PUCK.strokeWidth;
  const src = !cluster && photoUrl ? getProfilePhotoUrl(photoUrl, PUCK.photoPx) : null;
  const casing = `<circle cx="${cx}" cy="${cy}" r="${PUCK.casing}" fill="${PUCK.casingFill}"/>`;
  const face = `<circle cx="${cx}" cy="${cy}" r="${PUCK.face}" fill="${fill}" stroke="${PUCK.stroke}" stroke-width="${sw}"/>`;
  if (src) {
    return (
      casing +
      face +
      `<defs><clipPath id="${clipId}"><circle cx="${cx}" cy="${cy}" r="${PUCK.face}"/></clipPath></defs>` +
      `<image href="${esc(src)}" x="${cx - PUCK.face}" y="${cy - PUCK.face}" width="${PUCK.face * 2}" height="${PUCK.face * 2}" clip-path="url(#${clipId})" preserveAspectRatio="xMidYMid slice"/>`
    );
  }
  if (cluster) return casing + face;
  const { size, weight, spacing, fill: tf } = PUCK.initial;
  return (
    casing +
    face +
    `<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-size="${size}" font-weight="${weight}" letter-spacing="${spacing}" fill="${tf}">${esc(findPeerInitial(displayName || '?'))}</text>`
  );
}

const trackedRing = (cx, cy) =>
  `<circle cx="${cx}" cy="${cy}" r="${PUCK.ring}" fill="none" stroke="${PUCK.stroke}" stroke-opacity="${PUCK.ringOpacity}" stroke-width="1"/>`;

/** A single peer as a MapLibre marker's innerHTML. */
export function peerPuckSvg({ photoUrl, displayName, tracked, clipId }) {
  const c = PUCK.box / 2;
  return (
    `<svg width="${PUCK.box}" height="${PUCK.box}" viewBox="0 0 ${PUCK.box} ${PUCK.box}">` +
    (tracked ? trackedRing(c, c) : '') +
    faceSvg({ cx: c, cy: c, displayName, photoUrl, clipId }) +
    `</svg>`
  );
}

/** A cluster: a real member's face behind (their hue), the cluster mark in front, a count badge. */
export function clusterPuckSvg({ count, front, back, tracked, clipIdBase }) {
  const c = PUCK.box / 2;
  return (
    `<svg width="${PUCK.box}" height="${PUCK.box}" viewBox="0 0 ${PUCK.box} ${PUCK.box}">` +
    (tracked ? trackedRing(c, c) : '') +
    (back
      ? faceSvg({
          cx: c + 3,
          cy: c - 3,
          displayName: back.displayName,
          photoUrl: back.photoUrl,
          clipId: `${clipIdBase}-b`,
        })
      : '') +
    faceSvg({
      cx: c,
      cy: c,
      displayName: front?.displayName,
      photoUrl: null,
      clipId: `${clipIdBase}-f`,
      cluster: true,
    }) +
    `<circle cx="${c + PUCK.badgeOffset}" cy="${c - PUCK.badgeOffset}" r="${PUCK.badge}" fill="${PUCK.badgeFill}"/>` +
    `<text x="${c + PUCK.badgeOffset}" y="${c - PUCK.badgeOffset + 0.5}" text-anchor="middle" dominant-baseline="central" font-size="9" font-weight="700" fill="${PUCK.badgeText}">${count}</text>` +
    `</svg>`
  );
}

/**
 * Canvas painter for sprite textures. `k` = canvas px per SVG unit; (cx, cy) in canvas px.
 * `image` is an already-loaded HTMLImageElement or null; the initial is drawn when there is no image.
 */
export function drawFace(
  ctx,
  cx,
  cy,
  k,
  { displayName, image = null, cluster = false, inPin = false },
) {
  if (glassFaces()) {
    paintGlassFace(ctx, cx, cy, (inPin ? PUCK.face : PUCK.casing) * k, {
      displayName,
      image,
      cluster,
      inPin,
      initial: findPeerInitial(displayName || '?'),
    });
    return;
  }
  ctx.beginPath();
  ctx.arc(cx, cy, PUCK.casing * k, 0, Math.PI * 2);
  ctx.fillStyle = PUCK.casingFill;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, PUCK.face * k, 0, Math.PI * 2);
  ctx.fillStyle = cluster ? PUCK.clusterFill : getAvatarColorHex(displayName);
  ctx.fill();
  ctx.lineWidth = (cluster ? PUCK.clusterStrokeWidth : PUCK.strokeWidth) * k;
  ctx.strokeStyle = PUCK.stroke;
  ctx.stroke();
  if (image) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, PUCK.face * k, 0, Math.PI * 2);
    ctx.clip();
    const r = PUCK.face * k;
    const s = Math.max((2 * r) / image.width, (2 * r) / image.height);
    ctx.drawImage(
      image,
      cx - (image.width * s) / 2,
      cy - (image.height * s) / 2,
      image.width * s,
      image.height * s,
    );
    ctx.restore();
  } else if (!cluster) {
    ctx.fillStyle = PUCK.initial.fill;
    ctx.font = `${PUCK.initial.weight} ${PUCK.initial.size * k}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(findPeerInitial(displayName || '?'), cx, cy + 0.5 * k);
  }
}

export function drawTrackedRing(ctx, cx, cy, k) {
  ctx.beginPath();
  ctx.arc(cx, cy, PUCK.ring * k, 0, Math.PI * 2);
  ctx.lineWidth = 1 * k;
  ctx.strokeStyle = PUCK.stroke;
  ctx.globalAlpha = PUCK.ringOpacity;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

export function drawBadge(ctx, cx, cy, k, count) {
  const bx = cx + PUCK.badgeOffset * k,
    by = cy - PUCK.badgeOffset * k;
  ctx.beginPath();
  ctx.arc(bx, by, PUCK.badge * k, 0, Math.PI * 2);
  ctx.fillStyle = PUCK.badgeFill;
  ctx.fill();
  ctx.fillStyle = PUCK.badgeText;
  ctx.font = `700 ${9 * k}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(count), bx, by + 0.5 * k);
}
